import { useState } from 'react'
import { UserRole } from '@kfolks/types'
import { roleAtLeast } from '@/auth'
import { formatSize } from '@/utils/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
	FileText,
	Image,
	FileSpreadsheet,
	Presentation,
	Music,
	Video,
	Archive,
	File,
	Download,
	Share2,
	Trash2,
} from 'lucide-react'
import { useDeleteFile, useDownloadFile } from '@/hooks/useFiles'
import type { FileRecord } from '@/services/file-api'

const CATEGORY_ICONS: Record<string, React.ElementType> = {
	document: FileText,
	image: Image,
	spreadsheet: FileSpreadsheet,
	presentation: Presentation,
	audio: Music,
	video: Video,
	archive: Archive,
	other: File,
}

interface FileCardProps {
	file: FileRecord
	currentUserId?: string
	currentUserRole?: string
	onShare?: (fileId: string) => void
}

export function FileCard({ file, currentUserId, currentUserRole, onShare }: FileCardProps) {
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const downloadMutation = useDownloadFile()
	const deleteMutation = useDeleteFile()

	const isOwner = currentUserId === file.uploadedBy
	const isSuperAdmin = currentUserRole !== undefined && roleAtLeast(currentUserRole, UserRole.SUPER_ADMIN)
	const canManage = isOwner || isSuperAdmin

	const IconComponent = CATEGORY_ICONS[file.fileCategory ?? 'other'] ?? File

	const handleDownload = () => {
		downloadMutation.mutate(file.fileId)
	}

	const handleDelete = () => {
		deleteMutation.mutate(file.fileId, {
			onSuccess: () => setShowDeleteConfirm(false),
		})
	}

	return (
		<Card className="relative group" data-testid="file-card">
			<CardContent className="p-4">
				<div className="flex items-start gap-3">
					<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-500 flex-shrink-0">
						<IconComponent className="h-5 w-5" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-medium text-gray-900" title={file.fileName}>
							{file.fileName}
						</p>
						<p className="text-xs text-gray-500">
							{file.fileSize ? formatSize(file.fileSize) : 'Pending'} &middot;{' '}
							{new Date(file.createdAt).toLocaleDateString()}
						</p>
						{file.entityType && (
							<p className="mt-1 text-xs text-gray-400">
								{file.entityType}
								{file.entityId ? ` / ${file.entityId.substring(0, 8)}...` : ''}
							</p>
						)}
					</div>
				</div>

				<div className="mt-3 flex items-center gap-1">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleDownload}
						disabled={downloadMutation.isPending}
						title="Download"
					>
						<Download className="h-4 w-4" />
					</Button>

					{canManage && onShare && (
						<Button variant="ghost" size="sm" onClick={() => onShare(file.fileId)} title="Share">
							<Share2 className="h-4 w-4" />
						</Button>
					)}

					{canManage && (
						<>
							{showDeleteConfirm ? (
								<div className="ml-auto flex items-center gap-1">
									<span className="text-xs text-red-600">Delete?</span>
									<Button variant="destructive" size="sm" onClick={handleDelete} disabled={deleteMutation.isPending}>
										Yes
									</Button>
									<Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)}>
										No
									</Button>
								</div>
							) : (
								<Button
									variant="ghost"
									size="sm"
									onClick={() => setShowDeleteConfirm(true)}
									title="Delete"
									className="ml-auto text-red-500 hover:text-red-700"
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							)}
						</>
					)}
				</div>
			</CardContent>
		</Card>
	)
}
